import { useState, useCallback, useMemo } from 'react';

interface UseTableFilterStateOptions<TFilters> {
  initialFilters: TFilters;
  filterToSelectionMap: (filters: TFilters) => Record<string, string[]>;
  columnIdToFilterKey: (columnId: string) => string;
  // Optional external state control
  externalFilters?: TFilters;
  onExternalFilterChange?: (columnId: string, selectedValues: string[]) => void;
}

/**
 * Shared hook for managing table filter state across Invoice/Shipment/Charge tables
 *
 * Provides:
 * - Filter state management (Local or External/Controlled)
 * - Selection mapping (filter keys to display keys)
 * - Column filter handler
 *
 * @param initialFilters - Initial filter state (used if externalFilters is not provided)
 * @param filterToSelectionMap - Maps filter state to selection display format
 * @param columnIdToFilterKey - Maps column ID to filter key (handles aliases)
 * @param externalFilters - (Optional) Controlled filter state from parent
 * @param onExternalFilterChange - (Optional) Handler for external state updates
 */
export const useTableFilterState = <TFilters extends Record<string, any>>({
  initialFilters,
  filterToSelectionMap,
  columnIdToFilterKey,
  externalFilters,
  onExternalFilterChange,
}: UseTableFilterStateOptions<TFilters>) => {
  const [localFilters, setLocalFilters] = useState<TFilters>(initialFilters);

  // Determine effective filters: External (Controlled) or Local (Uncontrolled)
  const tableFilters = externalFilters ?? localFilters;

  // Map filters to selection format for column headers
  const filterSelections = useMemo(() => filterToSelectionMap(tableFilters), [tableFilters, filterToSelectionMap]);

  // Handle column filter changes
  const handleColumnFilter = useCallback(
    (columnId: string, selectedValues: string[]) => {
      if (onExternalFilterChange) {
        // Controlled mode: delegate to parent
        onExternalFilterChange(columnId, selectedValues);
      } else {
        // Uncontrolled mode: update local state
        const nextValues = selectedValues?.length ? selectedValues : [];
        const key = columnIdToFilterKey(columnId);

        setLocalFilters((prev) => ({
          ...prev,
          [key]: nextValues,
        }));
      }
    },
    [columnIdToFilterKey, onExternalFilterChange]
  );

  // Allow bulk override of all filters
  const overrideAllFilters = useCallback(
    (newFilters: TFilters) => {
      if (onExternalFilterChange) {
        // For controlled mode, we can't easily bulk update unless the parent supports it.
        // Current implementation assumes column-by-column for external.
        // If needed, we'd need to extend the external interface.
        // For now, this is primarily for the local state case in AnalyticsDataContext.
        console.warn('overrideAllFilters called in controlled mode - not fully supported');
      } else {
        setLocalFilters(newFilters);
      }
    },
    [onExternalFilterChange]
  );

  return {
    tableFilters,
    filterSelections,
    handleColumnFilter,
    overrideAllFilters,
  };
};
